// Predefined argument descriptions for common GraphQL field arguments
export const ARGUMENT_DESCRIPTIONS = {
  // Core identifiers
  'id': 'Unique identifier',
  'active': 'Filter to only include active records',
  'onlyenabled': 'Return only enabled items',
  'deleted': 'Include deleted records in results',
  'closed': 'Filter by closed status',
  'done': 'Filter by completion status',
  'declined': 'Include declined items',

  // Pagination and limits
  'limit': 'Maximum number of results to return',
  'offset': 'Number of results to skip for pagination',
  'after': 'Cursor for forward pagination',
  'before': 'Cursor for backward pagination',

  // Search and filtering
  'query': 'Search query text',
  'search': 'Search term to filter results',

  // Date and time
  'date': 'Specific date for the query',
  'startdate': 'Start of date range',
  'enddate': 'End of date range',
  'start': 'Starting point for the range',
  'end': 'Ending point for the range',
  'datetime': 'Specific date and time',

  // Contact information
  'email': 'Email address',
  'emails': 'List of email addresses',
  'emailto': 'Recipient email address',
  'emailbody': 'Content of the email message',
  'phone': 'Phone number',

  // Content and metadata
  'name': 'Name of the resource',
  'body': 'Main content or message body',
  'comment': 'Comment or note text',
  'notes': 'Additional notes or comments',

  // Financial
  'amount': 'Monetary amount',
  'discount': 'Discount value',
  'discountamount': 'Amount of discount to apply',
  'discounttype': 'Type of discount (percentage or fixed amount)',

  // Classification
  'category': 'Category classification',
  'categoryids': 'List of category IDs to filter by',
  'type': 'Type or classification',
  'status': 'Current status',
  'code': 'Unique code identifier',

  // System and configuration
  'areacode': 'Telephone area code',
  'enable': 'Enable or activate the feature',
  'consent': 'Consent agreement status',
  'donation': 'Donation amount or flag',
  'document': 'Document data or file',
  'documenttype': 'Type of document',
  'attachments': 'List of file attachments',
  'autorenew': 'Automatically renew on expiration',
  'action': 'Action to perform',

  // Reporting and analytics
  'allowhistorical': 'Include historical data in results',
  'additionalp rovideronly': 'Limit to additional providers only',
  'breakdownbyprovider': 'Group results by provider',
  'breakdowns': 'Breakdown categories for reporting',
  'byinvoicedate': 'Group or filter by invoice date',
  'agingbuckets': 'Time periods for aging analysis',
  'asondate': 'Calculate values as of this date',

  // Security and authentication
  'currentpassword': 'User\'s current password for verification',

  // Medical and clinical
  'drawtime': 'Time when sample was drawn',

  // System behavior
  'excludechildren': 'Exclude child records from results',
  'abortOnProbableTimeout': 'Cancel operation if timeout is likely'
};

// Veterinary-specific argument descriptions organized by category
const VETERINARY_DESCRIPTIONS = {
  // Patient/Client IDs
  'patientid': 'ID of the patient',
  'clientid': 'ID of the client (pet owner)',
  'coownerclientid': 'ID of the co-owner client',
  'clienttagids': 'List of client tag IDs to filter by',
  'clientconsentid': 'ID of the client consent record',

  // Appointment related
  'appointmentid': 'ID of the appointment',
  'apptid': 'ID of the appointment',
  'appointmentfromid': 'Source appointment ID for rescheduling',
  'appointmenttoid': 'Destination appointment ID for rescheduling',
  'appointmenttypeid': 'ID of the appointment type',
  'appointmenttypeids': 'List of appointment type IDs to filter by',
  'apptonly': 'Include only appointment-related data',
  'daycareonly': 'Include only daycare appointments',

  // Clinical/Medical
  'encounterid': 'ID of the clinical encounter',
  'encountersectionname': 'Name of the encounter section',
  'clinicalnoteid': 'ID of the clinical note',
  'diagnosticcode': 'Diagnostic or ICD code',
  'diagnosticcodeorglabtype': 'Organization lab type for diagnostic code',
  'careteamid': 'ID of the care team',
  'careteamroleid': 'ID of the care team role',

  // Billing/Financial
  'invoiceid': 'ID of the invoice',
  'estimateid': 'ID of the estimate',
  'creditmemoid': 'ID of the credit memo',
  'couponid': 'ID of the coupon',

  // Organization/Department
  'departmentid': 'ID of the department',
  'departmentids': 'List of department IDs to filter by',
  'destinationorgdepartmentid': 'Target organization department ID for transfer',
  'destinationrelationshipid': 'Target relationship ID for transfer',

  // Boarding/Hospitalization
  'boardgroupid': 'ID of the boarding group',
  'boardrowid': 'ID of the boarding row',

  // Communication
  'conversationid': 'ID of the conversation thread',
  'calllogid': 'ID of the call log entry',
  'contactmethod': 'Preferred method of contact',
  'enabledialpad': 'Enable phone dialpad functionality',

  // Custom fields
  'customappointmentfieldid': 'ID of the custom appointment field definition',
  'customclientfieldid': 'ID of the custom client field definition',
  'custompatientfieldid': 'ID of the custom patient field definition',

  // Forms and consent
  'consentformid': 'ID of the consent form',
  'cadenceid': 'ID of the reminder cadence',

  // Attachments and media
  'attachmentid': 'ID of the file attachment',
  'attachmentids': 'List of attachment IDs',
  'asset': 'Media asset or file',
  'clinicimage': 'Clinic logo or branding image',

  // Time tracking
  'clockin': 'Clock-in timestamp for timesheet',
  'clockout': 'Clock-out timestamp for timesheet',
  'endduedate': 'Final due date for the item',

  // Entity references
  'entity': 'Entity type or reference',
  'entityid': 'ID of the referenced entity',
  'entityname': 'Name of the entity',
  'entitytagid': 'ID of the entity tag',
  'entitytype': 'Type classification of the entity',
  'entitytypeid': 'ID of the entity type',

  // Data/input objects
  'datum': 'Data point or value',
  'datumidx': 'Index of the data point'
};

// Clean function to lookup veterinary descriptions
export const generateVeterinaryDescription = (name) => {
  return VETERINARY_DESCRIPTIONS[name] || null;
};

// Utility to convert camelCase to readable text
const camelToReadable = (text) => text.replace(/([A-Z])/g, ' $1').trim().toLowerCase();

// Pattern-based description generators
const PATTERN_GENERATORS = [
  {
    condition: (name, argType) => name === 'input' && argType && argType.name && argType.name.includes('Input'),
    generate: (name, argType) => {
      const typeName = camelToReadable(argType.name.replace('Input', ''));
      return `Input data for ${typeName} operation`;
    }
  },
  {
    condition: (name) => name.endsWith('id'),
    generate: (name) => {
      const resource = camelToReadable(name.replace(/id$/, ''));
      return `ID of the ${resource}`;
    }
  },
  {
    condition: (name) => name.endsWith('ids'),
    generate: (name) => {
      const resource = camelToReadable(name.replace(/ids$/, ''));
      return `List of ${resource} IDs`;
    }
  }
];

// Clean function to generate pattern-based descriptions
export const generatePatternDescription = (name, argType) => {
  for (const pattern of PATTERN_GENERATORS) {
    if (pattern.condition(name, argType)) {
      return pattern.generate(name, argType);
    }
  }
  return null;
};

// Main function to generate argument descriptions
export const generateArgumentDescription = (argName, argType) => {
  const name = argName.toLowerCase();

  // Check predefined descriptions first
  if (ARGUMENT_DESCRIPTIONS[name]) {
    return ARGUMENT_DESCRIPTIONS[name];
  }

  // Check veterinary-specific patterns
  const veterinaryDescription = generateVeterinaryDescription(name);
  if (veterinaryDescription) {
    return veterinaryDescription;
  }

  // Check generic patterns
  const patternDescription = generatePatternDescription(name, argType);
  if (patternDescription) {
    return patternDescription;
  }

  // Final fallback
  const readable = argName.replace(/([A-Z])/g, ' $1').trim();
  return readable.charAt(0).toUpperCase() + readable.slice(1).toLowerCase();
};



// WEBPACK FOOTER //
// ./src/utils/argumentDescriptions.js